import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  Flag,
  Loader2,
  UserRound,
  Wrench,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { api, getApiErrorMessage } from "../../api/client";
import { unwrap } from "../../lib/api-envelope";
import { setQueryDataMapped } from "../../lib/queryClient";
import { fmtCurrency, sumMoney } from "../../lib/display-labels";
import { useTenantTimeZone } from "../../hooks/useTenantTimeZone";
import { PageHeaderBand } from "../../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../../components/ui/LoadFailureNotice";
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from "../../lib/load-failure";
import { displayValue } from "../../features/fieldiq-handoff/contracts";
import { DhEmpty, DhPanel, DhSpec } from "../downhole/DownholeBits";
import {
  DASH,
  Part,
  StatusBadge,
  WO_STATUS,
  WorkOrder,
  WorkOrderApiRow,
  normalizeWorkOrder,
  numOrUndefined,
  priorityLabel,
  woSkin,
} from "./rental-work-order";

/**
 * One rental maintenance work order, /rentals/maintenance/:id: full page, the
 * layout of the rental field ticket page. Band (Priority / Opened / Assigned /
 * Total cost), the description, the checklist, parts, cost tracking and the
 * status actions.
 *
 * Was the right half of a split pane on /rentals/maintenance until #815; the
 * sections and the cost save moved here unchanged in behaviour.
 *
 * Every figure is what GET /surface-rentals/maintenance/work-orders/:id sent.
 * The total is the three stored components, all or nothing (normalizeWorkOrder).
 */

/**
 * The "$" sits inside the cost inputs. `!pl-7`, not `pl-6`: index.css declares
 * a plain `.px-3` after `@tailwind utilities`, so it beat the Input's `pl-6`
 * and the digits ran under the "$".
 */
const COST_INPUT = "h-8 !pl-7 text-sm";

type CostKey = "parts_cost" | "labour_cost" | "third_party_cost";

/** An absent cost is a blank field, never "0". */
const costText = (v: number | undefined): string => (v == null ? "" : String(v));

export default function RentalWorkOrderDetail() {
  const navigate = useNavigate();
  const { id = "" } = useParams<{ id: string }>();
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();

  const queryKey = ["rentals", "maintenance", "work-order", id, timeZone];
  const query = useQuery({
    // Under ["rentals", "maintenance"], so the register's refresh reaches it.
    queryKey,
    queryFn: async (): Promise<WorkOrder | null> => {
      try {
        const raw = unwrap<WorkOrderApiRow>((await api.rentals.maintenance.workOrders.get(id)).data);
        return raw && raw.id ? normalizeWorkOrder(raw, timeZone) : null;
      } catch (err) {
        // The API answers a work order that is not this company's, or is gone,
        // with 404. That is an answer — "no such work order" — not a failure.
        if ((err as { response?: { status?: number } })?.response?.status === 404) return null;
        throw err;
      }
    },
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const wo = query.data ?? null;
  const loading = query.isLoading && !failure;

  return (
    <div data-testid="rental-work-order-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Surface rentals · Work order"
        title={wo ? wo.wo_number : loading ? "Loading…" : "Work order"}
        onBack={() => navigate("/rentals/maintenance")}
        backLabel="Back to Maintenance"
        stats={
          !wo
            ? undefined
            : [
                { icon: <Flag className="size-4" aria-hidden="true" />, label: "Priority", sub: "As set", tone: "accent", value: priorityLabel(wo.priority) },
                { icon: <CalendarDays className="size-4" aria-hidden="true" />, label: "Opened", sub: "Raised on", tone: "info", value: wo.opened_date },
                { icon: <UserRound className="size-4" aria-hidden="true" />, label: "Assigned", sub: "Crew member", tone: "neutral", value: wo.assigned_to },
                { icon: <CircleDollarSign className="size-4" aria-hidden="true" />, label: "Total cost", sub: "Parts, labour, third party", tone: "success", value: fmtCurrency(wo.total_cost) },
              ]
        }
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading work order" />
            </div>
          ) : !wo ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              {failure ? (
                <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this work order" />
              ) : (
                <DhEmpty>This work order does not exist, or it was deleted.</DhEmpty>
              )}
            </div>
          ) : (
            // Keyed by id: the cost fields load once per work order, and again
            // after a save — never on a background refetch, which would wipe
            // what the user is typing.
            <WorkOrderBody
              key={wo.id}
              wo={wo}
              timeZone={timeZone}
              onSaved={(next) => setQueryDataMapped<WorkOrder>(queryClient, queryKey, next)}
              refresh={() => queryClient.invalidateQueries({ queryKey: ["rentals", "maintenance"] })}
            />
          )}
        </div>
      </section>
    </div>
  );
}

function WorkOrderBody({
  wo,
  timeZone,
  onSaved,
  refresh,
}: {
  wo: WorkOrder;
  timeZone: string | null;
  onSaved: (next: WorkOrder) => void;
  refresh: () => Promise<unknown>;
}) {
  // Cost tracking. Held as TEXT so an absent cost is a blank field, not a 0
  // (#815: `?? 0` loaded an absent cost as 0 and Save wrote it back as 0).
  const [costPartsCost, setCostPartsCost] = useState(() => costText(wo.parts_cost));
  const [costLabourCost, setCostLabourCost] = useState(() => costText(wo.labour_cost));
  const [costThirdPartyCost, setCostThirdPartyCost] = useState(() => costText(wo.third_party_cost));
  const [costNotes, setCostNotes] = useState(() => wo.cost_notes ?? "");
  const [costSaving, setCostSaving] = useState(false);

  const loadCosts = (w: WorkOrder) => {
    setCostPartsCost(costText(w.parts_cost));
    setCostLabourCost(costText(w.labour_cost));
    setCostThirdPartyCost(costText(w.third_party_cost));
    setCostNotes(w.cost_notes ?? "");
  };

  // All or nothing, as in normalizeWorkOrder: a blank part makes the total a dash.
  const costTotal = (() => {
    const parts = [costPartsCost, costLabourCost, costThirdPartyCost].map((v) => (v.trim() === "" ? NaN : Number(v)));
    return parts.every(Number.isFinite) ? parts.reduce((a, b) => a + b, 0) : null;
  })();

  /**
   * PATCH /maintenance/work-orders/:id/costs: absent keeps, a value sets,
   * null clears (api#980). So it sends ONLY the fields that changed: an
   * untouched field is left out and keeps its value (it used to be re-sent,
   * as 0 when it had loaded blank). A field that had a value and is now blank
   * is sent as null, which clears it back to "no cost yet" — it used to be
   * refused here, while the API kept the old value whatever was sent.
   */
  const handleSaveCosts = async () => {
    const body: { parts_cost?: number | null; labour_cost?: number | null; third_party_cost?: number | null; cost_notes?: string | null } = {};
    const fields: [CostKey, string, number | undefined][] = [
      ["parts_cost", costPartsCost, wo.parts_cost],
      ["labour_cost", costLabourCost, wo.labour_cost],
      ["third_party_cost", costThirdPartyCost, wo.third_party_cost],
    ];
    for (const [key, text, before] of fields) {
      const label = key === "parts_cost" ? "Parts cost" : key === "labour_cost" ? "Labour cost" : "Third-party cost";
      if (text.trim() === "") {
        if (before != null) body[key] = null;
        continue;
      }
      const n = Number(text);
      if (!Number.isFinite(n) || n < 0) {
        toast.error(`${label} must be a number, 0 or more.`);
        return;
      }
      if (n !== before) body[key] = n;
    }
    if (costNotes.trim() === "") {
      if (wo.cost_notes) body.cost_notes = null;
    } else if (costNotes !== (wo.cost_notes ?? "")) {
      body.cost_notes = costNotes;
    }
    if (Object.keys(body).length === 0) {
      toast.error("Nothing has changed.");
      return;
    }
    setCostSaving(true);
    try {
      const res = await api.rentals.maintenance.workOrders.updateCosts(wo.id, body);
      const row = (res?.data as { data?: WorkOrderApiRow } | undefined)?.data;
      if (row) {
        const fresh = normalizeWorkOrder({ ...row, asset_name: wo.asset_name, asset_number: wo.asset_tag } as WorkOrderApiRow, timeZone);
        const next = { ...wo, parts_cost: fresh.parts_cost, labour_cost: fresh.labour_cost, third_party_cost: fresh.third_party_cost, total_cost: fresh.total_cost, cost_notes: fresh.cost_notes };
        onSaved(next);
        loadCosts(next);
      }
      await refresh();
      toast.success("Costs saved");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to save costs"));
    } finally {
      setCostSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      await api.rentals.maintenance.workOrders.updateStatus(wo.id, { status: newStatus });
      toast.success(`Work order ${wo.wo_number} updated to ${WO_STATUS[newStatus]?.label || newStatus}`);
      onSaved({ ...wo, status: newStatus as WorkOrder["status"] });
      await refresh();
    } catch (err) {
      // The page keeps the status the server still holds. The API refuses an
      // illegal transition by name ("Cannot transition from 'open' to
      // 'approved'"), which is more useful than any copy written here.
      toast.error(getApiErrorMessage(err, "Failed to update work order status"));
    }
  };

  /*
    There is no checklist toggle. It flipped `completed` in local state and
    issued no request — the API has GET, POST, PATCH /:id/status and
    PATCH /:id/costs on a work order and nothing that writes `checklist_json`
    after creation, so a ticked box survived until the next refetch and then
    silently un-ticked itself. The boxes are read-only until an endpoint
    exists. Do not restore the handler without one.
  */

  const partLine = (p: Part): number | null => {
    const qty = numOrUndefined(p.qty);
    const cost = numOrUndefined(p.cost);
    return qty === undefined || cost === undefined ? null : qty * cost;
  };

  const costInput = (id: string, label: string, value: string, set: (v: string) => void) => (
    <div>
      <Label htmlFor={id} className="text-[10px] text-fieldiq-muted">{label}</Label>
      <div className="relative mt-1">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-fieldiq-muted">$</span>
        <Input type="number" className={COST_INPUT} id={id} min="0" value={value} onChange={(e) => set(e.target.value)} />
      </div>
    </div>
  );

  return (
    <>
      <DhPanel eyebrow="Work order" title="Work order record" testId="rental-work-order-record">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 p-4 sm:grid-cols-2 md:grid-cols-4">
          <DhSpec label="Work order #" value={wo.wo_number} mono />
          <DhSpec label="Status" value={<StatusBadge skin={woSkin(wo.status)} />} />
          <DhSpec label="Asset" value={wo.asset_name} />
          <DhSpec label="Asset #" value={wo.asset_tag} mono />
        </dl>
        <div className="border-t border-fieldiq-border px-4 py-4">
          <h3 className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Description</h3>
          <p className="text-sm leading-relaxed text-fieldiq-text">{wo.description}</p>
        </div>
      </DhPanel>

      <DhPanel
        eyebrow="Checklist"
        title={`Checklist (${wo.checklist.filter((c) => c.completed).length}/${wo.checklist.length})`}
        testId="rental-work-order-checklist"
        footer={wo.checklist.length > 0 ? <>Read-only &mdash; there is no endpoint to save a ticked item yet.</> : undefined}
      >
        {wo.checklist.length === 0 ? (
          <DhEmpty>No checklist on this work order.</DhEmpty>
        ) : (
          <div className="space-y-2 p-4">
            {wo.checklist.map((item) => (
              <div key={item.id} className="flex items-center gap-3 rounded-md p-2">
                <Checkbox checked={item.completed} disabled />
                <span className={`text-sm ${item.completed ? "text-fieldiq-muted line-through" : "text-fieldiq-text"}`}>
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        )}
      </DhPanel>

      {wo.parts.length > 0 && (
        <DhPanel eyebrow="Parts" title="Parts" testId="rental-work-order-parts">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                <tr className="text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                  <th scope="col" className="px-4 py-3">Part</th>
                  <th scope="col" className="px-4 py-3 text-right">Qty</th>
                  <th scope="col" className="px-4 py-3 text-right">Cost</th>
                </tr>
              </thead>
              <tbody className="text-fieldiq-text">
                {wo.parts.map((p, i) => (
                  <tr key={i} className="border-t border-fieldiq-border">
                    <td className="px-4 py-2">{p.name || DASH}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{displayValue(numOrUndefined(p.qty))}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmtCurrency(p.cost)}</td>
                  </tr>
                ))}
                <tr className="border-t border-fieldiq-border font-bold">
                  <td className="px-4 py-2">Total</td>
                  <td />
                  {/* All or nothing: one part with no qty or cost and the total is a dash. */}
                  <td className="px-4 py-2 text-right tabular-nums">{fmtCurrency(sumMoney(wo.parts.map(partLine)))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </DhPanel>
      )}

      <DhPanel eyebrow="Costs" title="Cost tracking" testId="rental-work-order-costs">
        <div className="p-4">
          {wo.status === "in_progress" || wo.status === "pending_approval" ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {costInput("wo-cost-parts", "Parts Cost", costPartsCost, setCostPartsCost)}
                {costInput("wo-cost-labour", "Labour Cost", costLabourCost, setCostLabourCost)}
                {costInput("wo-cost-third", "Third Party", costThirdPartyCost, setCostThirdPartyCost)}
              </div>
              <div className="flex items-center justify-between rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/50 p-2.5">
                <span className="text-xs font-bold text-fieldiq-muted">Total Cost</span>
                <span data-testid="wo-cost-total" className="text-sm font-bold tabular-nums text-fieldiq-text">{fmtCurrency(costTotal)}</span>
              </div>
              <div>
                <Label htmlFor="wo-cost-notes" className="text-[10px] text-fieldiq-muted">Cost Notes</Label>
                <Textarea
                  id="wo-cost-notes"
                  className="mt-1 text-sm"
                  rows={2}
                  placeholder="Subcontractor details, shop name, etc."
                  value={costNotes}
                  onChange={(e) => setCostNotes(e.target.value)}
                />
              </div>
              <Button size="sm" onClick={handleSaveCosts} disabled={costSaving}>
                {costSaving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Save Costs
              </Button>
            </div>
          ) : wo.status === "approved" ? (
            <div className="space-y-2">
              <dl className="grid grid-cols-3 gap-3">
                <DhSpec label="Parts" value={fmtCurrency(wo.parts_cost)} mono />
                <DhSpec label="Labour" value={fmtCurrency(wo.labour_cost)} mono />
                <DhSpec label="Third Party" value={fmtCurrency(wo.third_party_cost)} mono />
              </dl>
              <div className="flex items-center justify-between rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/50 p-2.5">
                <span className="text-xs font-bold text-fieldiq-muted">Total Cost</span>
                <span data-testid="wo-total-cost" className="text-sm font-bold tabular-nums text-fieldiq-text">{fmtCurrency(wo.total_cost)}</span>
              </div>
              {wo.cost_notes && <p className="text-xs italic text-fieldiq-muted">{wo.cost_notes}</p>}
            </div>
          ) : (
            <p className="text-xs text-fieldiq-muted">Cost entry available when work is in progress</p>
          )}
        </div>
      </DhPanel>

      {/*
        No Status History section. `maintenance_work_orders` has no history
        column, no audit table stands behind it and no endpoint returns one.
        Restore it when the API records transitions; a heading over nothing
        reads as a load failure.
      */}

      <div className="flex flex-wrap gap-2">
        {wo.status === "open" && (
          <Button onClick={() => handleStatusChange("in_progress")}>
            <Wrench className="mr-1.5 h-4 w-4" />
            Start Work
          </Button>
        )}
        {wo.status === "in_progress" && (
          <Button onClick={() => handleStatusChange("pending_approval")}>
            <CheckCircle2 className="mr-1.5 h-4 w-4" />
            Submit for Approval
          </Button>
        )}
        {wo.status === "pending_approval" && (
          <Button onClick={() => handleStatusChange("approved")}>
            <CheckCircle2 className="mr-1.5 h-4 w-4" />
            Approve
          </Button>
        )}
        {wo.status === "approved" && (
          <span className="inline-flex items-center rounded-fi-pill border border-black/20 bg-fi-signal-green px-3 py-1.5 text-xs font-black text-fi-signal-ink">
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
            Completed &amp; Approved
          </span>
        )}
      </div>
    </>
  );
}
